'use client';

import { useRef, useCallback, useMemo, useState, useEffect } from 'react';
import { Box, HStack, css, token } from '@terra/ui-styled-system';
import { IconButton } from '../../components/icon-button';
import { Tooltip } from '../../components/tooltip';
import { MarkdownPreview } from '../markdown-preview';
import { useContainerMinWidthLayout } from '../useContainerMinWidthLayout';
import {
  TextBoldIcon,
  CheckmarkSquare01Icon,
  SourceCodeIcon,
  DragDropHorizontalIcon,
  Heading01Icon,
  Heading02Icon,
  Heading03Icon,
  TextItalicIcon,
  Link01Icon,
  LeftToRightListBulletIcon,
  LeftToRightListNumberIcon,
  QuoteDownIcon,
  TextStrikethroughIcon,
} from '@terra/ui-icons';

type RichMarkdownEditorProps = {
  /** The markdown content value */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Height of the editor */
  height?: number;
  /** Minimum height of the editor */
  minHeight?: number;
  /** Maximum height of the editor (for resize) */
  maxHeight?: number;
  /** Placeholder text when empty */
  placeholder?: string;
  /** Whether the editor is read-only */
  readOnly?: boolean;
  /** Whether to hide the toolbar */
  hideToolbar?: boolean;
  /** Preview mode: 'edit' (no preview), 'live' (side-by-side), 'preview' (preview only) */
  preview?: 'edit' | 'preview' | 'live';
  /** Whether to allow resizing the editor height */
  resizable?: boolean;
  /** Callback when height changes (for controlled resize) */
  onHeightChange?: (height: number) => void;
  stackBelowWidthPx?: number;
};
type MarkdownCommand = {
  icon: React.ElementType;
  label: string;
  prefix: string;
  suffix: string;
  block?: boolean;
};
const markdownCommands: Record<string, MarkdownCommand> = {
  bold: { icon: TextBoldIcon, label: 'Bold', prefix: '**', suffix: '**' },
  italic: { icon: TextItalicIcon, label: 'Italic', prefix: '_', suffix: '_' },
  strikethrough: {
    icon: TextStrikethroughIcon,
    label: 'Strikethrough',
    prefix: '~~',
    suffix: '~~',
  },
  link: { icon: Link01Icon, label: 'Link', prefix: '[', suffix: '](url)' },
  quote: {
    icon: QuoteDownIcon,
    label: 'Quote',
    prefix: '> ',
    suffix: '',
    block: true,
  },
  code: { icon: SourceCodeIcon, label: 'Code', prefix: '`', suffix: '`' },
  h1: {
    icon: Heading01Icon,
    label: 'Heading 1',
    prefix: '# ',
    suffix: '',
    block: true,
  },
  h2: {
    icon: Heading02Icon,
    label: 'Heading 2',
    prefix: '## ',
    suffix: '',
    block: true,
  },
  h3: {
    icon: Heading03Icon,
    label: 'Heading 3',
    prefix: '### ',
    suffix: '',
    block: true,
  },
  ul: {
    icon: LeftToRightListBulletIcon,
    label: 'Bullet List',
    prefix: '- ',
    suffix: '',
    block: true,
  },
  ol: {
    icon: LeftToRightListNumberIcon,
    label: 'Numbered List',
    prefix: '1. ',
    suffix: '',
    block: true,
  },
  checklist: {
    icon: CheckmarkSquare01Icon,
    label: 'Checklist',
    prefix: '- [ ] ',
    suffix: '',
    block: true,
  },
};
const textareaStyles = css({
  width: '100%',
  height: '100%',
  padding: '12px',
  border: 'none',
  outline: 'none',
  resize: 'none',
  backgroundColor: 'transparent',
  color: 'var(--colors-fg-default)',
  fontFamily: token('fonts.mono'),
  fontSize: '13px',
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  '&::placeholder': {
    color: 'var(--colors-fg-muted)',
  },
});
// Hide scrollbar but keep scrolling enabled
const hiddenScrollbarStyles = css({
  scrollbarWidth: 'none', // Firefox
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome/Safari/Opera
  },
});

export function RichMarkdownEditor({
  value,
  onChange,
  height = 200,
  minHeight = 100,
  maxHeight = 800,
  placeholder = 'Enter markdown content...',
  readOnly = false,
  hideToolbar = false,
  preview = 'live',
  resizable = false,
  onHeightChange,
  stackBelowWidthPx,
}: RichMarkdownEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previewScrollRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef<'editor' | 'preview' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentHeight, setCurrentHeight] = useState(height);
  const [isResizing, setIsResizing] = useState(false);
  const { setContainerRef, meetsMinWidth } = useContainerMinWidthLayout(
    stackBelowWidthPx ?? 0,
  );
  const isWideEditor = stackBelowWidthPx == null || meetsMinWidth;

  const setContainerNode = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      setContainerRef(node);
    },
    [setContainerRef],
  );
  const showEditor = preview === 'edit' || preview === 'live';
  const showPreview = preview === 'preview' || preview === 'live';
  const editorHeight = useMemo(() => {
    const h = resizable ? currentHeight : height;

    if (minHeight && h < minHeight) {
      return minHeight;
    }

    if (maxHeight && h > maxHeight) {
      return maxHeight;
    }

    return h;
  }, [height, minHeight, maxHeight, currentHeight, resizable]);

  // Handle resize drag
  useEffect(() => {
    if (!resizable || !isResizing) return;
    const handleMouseMove = (event: MouseEvent) => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newHeight = event.clientY - containerRect.top;
      const clampedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));

      setCurrentHeight(clampedHeight);
      onHeightChange?.(clampedHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizable, minHeight, maxHeight, onHeightChange]);

  // Synchronized scrolling between editor (textarea) and preview
  useEffect(() => {
    const textarea = textareaRef.current;
    const previewScroll = previewScrollRef.current;

    if (!textarea || !previewScroll || !showEditor || !showPreview) return;
    const syncScroll = (source: 'editor' | 'preview') => {
      if (isScrollingRef.current && isScrollingRef.current !== source) return;
      isScrollingRef.current = source;
      const sourceEl = source === 'editor' ? textarea : previewScroll;
      const targetEl = source === 'editor' ? previewScroll : textarea;
      const maxScroll = sourceEl.scrollHeight - sourceEl.clientHeight;
      const scrollPercentage =
        maxScroll > 0 ? sourceEl.scrollTop / maxScroll : 0;

      if (isFinite(scrollPercentage)) {
        const targetMaxScroll = targetEl.scrollHeight - targetEl.clientHeight;

        targetEl.scrollTop = scrollPercentage * targetMaxScroll;
      }

      // Reset after a short delay to allow the other scroll event to be ignored
      requestAnimationFrame(() => {
        isScrollingRef.current = null;
      });
    };

    const handleEditorScroll = () => syncScroll('editor');
    const handlePreviewScroll = () => syncScroll('preview');

    textarea.addEventListener('scroll', handleEditorScroll);
    previewScroll.addEventListener('scroll', handlePreviewScroll);

    return () => {
      textarea.removeEventListener('scroll', handleEditorScroll);
      previewScroll.removeEventListener('scroll', handlePreviewScroll);
    };
  }, [showEditor, showPreview]);
  // Check if text is already wrapped with the given prefix/suffix
  const isTextFormatted = useCallback(
    (
      text: string,
      start: number,
      end: number,
      prefix: string,
      suffix: string,
    ): {
      isFormatted: boolean;
      outerStart: number;
      outerEnd: number;
    } => {
      // Check if the selection or surrounding text already has the formatting
      const prefixLen = prefix.length;
      const suffixLen = suffix.length;
      // Check if text before selection starts with prefix and text after ends with suffix
      const beforeStart = Math.max(0, start - prefixLen);
      const afterEnd = Math.min(text.length, end + suffixLen);
      const textBefore = text.substring(beforeStart, start);
      const textAfter = text.substring(end, afterEnd);

      if (textBefore === prefix && textAfter === suffix) {
        return {
          isFormatted: true,
          outerStart: beforeStart,
          outerEnd: afterEnd,
        };
      }

      return { isFormatted: false, outerStart: start, outerEnd: end };
    },
    [],
  );
  // Apply markdown formatting to selected text (with toggle support)
  const applyCommand = useCallback(
    (command: MarkdownCommand) => {
      const textarea = textareaRef.current;

      if (!textarea) return;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = value.substring(start, end);
      let newText: string;
      let newCursorStart: number;
      let newCursorEnd: number;

      if (command.block) {
        // For block-level commands, ensure we're at the start of a line
        const beforeSelection = value.substring(0, start);
        const lineStart = beforeSelection.lastIndexOf('\n') + 1;
        const textBeforeCursor = value.substring(lineStart, start);

        if (selectedText) {
          // Check if all lines already have the prefix - if so, remove it
          const lines = selectedText.split('\n');
          const allLinesFormatted = lines.every((line) =>
            line.startsWith(command.prefix),
          );

          if (allLinesFormatted) {
            // Remove the prefix from all lines (toggle off)
            const unformattedLines = lines.map((line) =>
              line.substring(command.prefix.length),
            );

            newText =
              value.substring(0, start) +
              unformattedLines.join('\n') +
              value.substring(end);
            newCursorStart = start;
            newCursorEnd = start + unformattedLines.join('\n').length;
          } else {
            // Add prefix to lines that don't have it
            const formattedLines = lines.map((line) =>
              line.startsWith(command.prefix) ? line : command.prefix + line,
            );

            newText =
              value.substring(0, start) +
              formattedLines.join('\n') +
              value.substring(end);
            newCursorStart = start;
            newCursorEnd = start + formattedLines.join('\n').length;
          }
        } else {
          // No selection - check if current line starts with prefix
          const lineEnd = value.indexOf('\n', start);
          const actualLineEnd = lineEnd === -1 ? value.length : lineEnd;
          const currentLine = value.substring(lineStart, actualLineEnd);

          if (currentLine.startsWith(command.prefix)) {
            // Remove the prefix (toggle off)
            newText =
              value.substring(0, lineStart) +
              currentLine.substring(command.prefix.length) +
              value.substring(actualLineEnd);
            newCursorStart = Math.max(lineStart, start - command.prefix.length);
            newCursorEnd = newCursorStart;
          } else if (textBeforeCursor.trim() === '') {
            // Already at line start or only whitespace before
            newText =
              value.substring(0, lineStart) +
              command.prefix +
              value.substring(lineStart);
            newCursorStart = lineStart + command.prefix.length;
            newCursorEnd = newCursorStart;
          } else {
            // In the middle of a line - add newline first
            newText =
              value.substring(0, start) +
              '\n' +
              command.prefix +
              value.substring(start);
            newCursorStart = start + 1 + command.prefix.length;
            newCursorEnd = newCursorStart;
          }
        }
      } else {
        // Inline formatting with toggle support
        const { isFormatted, outerStart, outerEnd } = isTextFormatted(
          value,
          start,
          end,
          command.prefix,
          command.suffix,
        );

        if (isFormatted && selectedText) {
          // Remove the formatting (toggle off)
          newText =
            value.substring(0, outerStart) +
            selectedText +
            value.substring(outerEnd);
          newCursorStart = outerStart;
          newCursorEnd = outerStart + selectedText.length;
        } else if (selectedText) {
          // Wrap selection with prefix/suffix
          newText =
            value.substring(0, start) +
            command.prefix +
            selectedText +
            command.suffix +
            value.substring(end);
          newCursorStart = start + command.prefix.length;
          newCursorEnd = newCursorStart + selectedText.length;
        } else {
          // No selection - insert prefix/suffix and place cursor between
          newText =
            value.substring(0, start) +
            command.prefix +
            command.suffix +
            value.substring(end);
          newCursorStart = start + command.prefix.length;
          newCursorEnd = newCursorStart;
        }
      }

      onChange(newText);
      // Restore focus and selection after state update
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(newCursorStart, newCursorEnd);
      });
    },
    [value, onChange, isTextFormatted],
  );
  // Prevent toolbar buttons from stealing focus
  const handleToolbarMouseDown = useCallback(
    (event: React.MouseEvent, commandKey: string) => {
      event.preventDefault();
      const command = markdownCommands[commandKey];

      if (command) {
        applyCommand(command);
      }
    },
    [applyCommand],
  );
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(event.target.value);
    },
    [onChange],
  );
  const toolbarHeight = hideToolbar ? 0 : 36;
  const resizeHandleHeight = resizable ? 20 : 0;

  return (
    <Box
      ref={setContainerNode}
      h={editorHeight}
      w="full"
      display="flex"
      flexDirection="column"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="md"
      overflow="hidden"
      bg="bg.default"
      _focusWithin={{
        borderColor: 'colorPalette.default',
      }}
    >
      {/* Toolbar */}
      {!hideToolbar && (
        <HStack
          gap={0.5}
          px={2}
          py={1}
          bg="bg.subtle"
          borderBottomWidth="1px"
          borderColor="border.default"
          flexShrink={0}
          h={`${toolbarHeight}px`}
        >
          {/* Text formatting */}
          {(['bold', 'italic', 'strikethrough'] as const).map((key) => {
            const cmd = markdownCommands[key];
            const Icon = cmd.icon;

            return (
              <Tooltip key={key} title={cmd.label}>
                <IconButton
                  variant="ghost"
                  size="xs"
                  aria-label={cmd.label}
                  onMouseDown={(e) => handleToolbarMouseDown(e, key)}
                  disabled={readOnly}
                >
                  <Icon size={16} />
                </IconButton>
              </Tooltip>
            );
          })}

          <Box w="1px" h="20px" bg="border.subtle" mx={1} />

          {/* Headings */}
          {(['h1', 'h2', 'h3'] as const).map((key) => {
            const cmd = markdownCommands[key];
            const Icon = cmd.icon;

            return (
              <Tooltip key={key} title={cmd.label}>
                <IconButton
                  variant="ghost"
                  size="xs"
                  aria-label={cmd.label}
                  onMouseDown={(e) => handleToolbarMouseDown(e, key)}
                  disabled={readOnly}
                >
                  <Icon size={16} />
                </IconButton>
              </Tooltip>
            );
          })}

          <Box w="1px" h="20px" bg="border.subtle" mx={1} />

          {/* Links and code */}
          {(['link', 'code', 'quote'] as const).map((key) => {
            const cmd = markdownCommands[key];
            const Icon = cmd.icon;

            return (
              <Tooltip key={key} title={cmd.label}>
                <IconButton
                  variant="ghost"
                  size="xs"
                  aria-label={cmd.label}
                  onMouseDown={(e) => handleToolbarMouseDown(e, key)}
                  disabled={readOnly}
                >
                  <Icon size={16} />
                </IconButton>
              </Tooltip>
            );
          })}

          <Box w="1px" h="20px" bg="border.subtle" mx={1} />

          {/* Lists */}
          {(['ul', 'ol', 'checklist'] as const).map((key) => {
            const cmd = markdownCommands[key];
            const Icon = cmd.icon;

            return (
              <Tooltip key={key} title={cmd.label}>
                <IconButton
                  variant="ghost"
                  size="xs"
                  aria-label={cmd.label}
                  onMouseDown={(e) => handleToolbarMouseDown(e, key)}
                  disabled={readOnly}
                >
                  <Icon size={16} />
                </IconButton>
              </Tooltip>
            );
          })}
        </HStack>
      )}

      {/* Editor and Preview */}
      <Box
        display="flex"
        flex={1}
        overflow="hidden"
        gap={0}
        minH={0}
        flexDirection={isWideEditor ? 'row' : 'column'}
      >
        {showEditor && (
          <Box
            flex={isWideEditor ? 1 : '1 1 0'}
            overflow="hidden"
            bg="bg.default"
            minH={0}
            h={isWideEditor ? 'full' : undefined}
            borderBottomWidth={showPreview && !isWideEditor ? '1px' : '0'}
            borderRightWidth={showPreview && isWideEditor ? '1px' : '0'}
            borderColor="border.default"
          >
            <textarea
              ref={textareaRef}
              value={value}
              placeholder={placeholder}
              onChange={handleChange}
              readOnly={readOnly}
              className={`${textareaStyles} ${showPreview ? hiddenScrollbarStyles : ''}`}
              style={{
                height: '100%',
                overflowY: 'auto',
              }}
            />
          </Box>
        )}

        {/* Preview */}
        {showPreview && (
          <Box
            flex={isWideEditor ? 1 : '1 1 0'}
            overflow="hidden"
            bg="bg.default"
            minH={0}
            h={isWideEditor ? 'full' : undefined}
          >
            <Box
              ref={previewScrollRef}
              h="full"
              overflowY="auto"
              overflowX="hidden"
            >
              <Box p={3}>
                {value ? (
                  <MarkdownPreview source={value} fontSize={13} />
                ) : (
                  <Box color="fg.muted" fontSize="13px">
                    {placeholder}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Resize Handle */}
      {resizable && (
        <Box
          h={`${resizeHandleHeight}px`}
          w="full"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="bg.subtle"
          borderTopWidth="1px"
          borderColor="border.default"
          cursor="ns-resize"
          flexShrink={0}
          onMouseDown={() => setIsResizing(true)}
          _hover={{
            bg: 'bg.muted',
          }}
          userSelect="none"
        >
          <DragDropHorizontalIcon size={14} color="var(--colors-fg-muted)" />
        </Box>
      )}
    </Box>
  );
}
